import {Component, computed, inject, input, signal, OnInit} from '@angular/core';
import {StyleClass} from 'primeng/styleclass';
import {Router, RouterLink, RouterLinkActive} from '@angular/router';
import {SidebarData} from '../interface/sidebar';
import {AuthService} from '../../service/authservice/auth.service';
import {NgClass} from '@angular/common';
import {Avatar} from 'primeng/avatar';
import {UserService, CurrentUser} from '../../service/user.service';
import {take} from 'rxjs/operators';

interface UserProfile {
  name: string;
  email: string;
  picture: string | null;
}

@Component({
  selector: 'app-sidebar',
  imports: [
    StyleClass,
    RouterLink,
    RouterLinkActive,
    NgClass,
    Avatar
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  standalone: true
})
export class Sidebar implements OnInit {

  private router = inject(Router);
  private authService = inject(AuthService);
  private userService = inject(UserService);

  sidebarConfig = input<SidebarData>();
  settingsExpanded = signal(this.router.url.includes('/settings'));

  currentUser = signal<CurrentUser | null>(null);
  isLoading = signal(false);
  error = signal<string | null>(null);

  sidebarData = computed(() => {
    const config = this.sidebarConfig();
    return {
      mainItems: config?.items ?? [],
      settingsItems: config?.settingsItems ?? []
    };
  });

  userProfile = computed<UserProfile>(() => {
    const user = this.currentUser();
    
    if (user) {
      return {
        name: user.name || user.username || 'User',
        email: user.email || '',
        picture: null
      };
    }
    
    const claims = this.authService.getProfile() as Record<string, unknown> | null;
    if (claims) {
      return {
        name: (claims['name'] || claims['preferred_username']) as string || 'User',
        email: (claims['email'] as string) || '',
        picture: (claims['picture'] as string) || null
      };
    }
    return { name: 'User', email: '', picture: null };
  });

  ngOnInit(): void {
    this.fetchCurrentUser();
  }

  fetchCurrentUser(): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.userService.getCurrentUser().pipe(take(1)).subscribe({
      next: (userData) => {
        this.currentUser.set(userData);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load user profile');
        this.isLoading.set(false);
      }
    });
  }

  toggleSettings() {
    this.settingsExpanded.update(v => !v);
  }

  logout(): void {
    this.authService.logout();
  }
}